import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAccessControl from "../context/useAccessControl";
import {
  createInternalUserInvitation,
  deactivateCompanyUser,
  listCompanyUsers,
  updateCompanyUserAccess,
} from "../../services/supabase/companyAdminService";
import {
  buildDelegatedMozzoRoleBranchPermissions,
  buildMozzoRoleBranchPermissions,
  canAssignMozzoInternalUserRole,
  canDelegateMozzoBranchPermission,
  canViewMozzoPersonnelRole,
  getMozzoInternalUserRole,
  getMozzoInternalUserRoleLabel,
  listMozzoAssignableInternalUserRolesForAccessRole,
  MOZZO_BRANCH_PERMISSION_GROUPS,
  resolveMozzoInternalUserRoleKey,
  updateMozzoBranchPermissionGroup,
} from "../../services/local/accessControl";
import {
  STAFF_CHANGE_STATUSES,
  STAFF_CHANGE_TYPES,
  createPersonnelChangeRequest,
  deliverApprovedPersonnelInvitation,
  getLocalOperationalStaffStates,
  listBranchPersonnel,
  listPersonnelChangeRequests,
  reviewPersonnelChangeRequest,
  setOperationalStaffActive,
} from "../../services/supabase/staffManagementService";
import {
  isLocalPersonnelTestHarnessEnabled,
  listLocalPersonnelTestProfiles,
  removeLocalPersonnelTestProfile,
  saveLocalPersonnelTestProfile,
} from "../../services/local/personnelTestHarness";

const ROLE_ORDER = [
  "admin_general",
  "encargado_sucursal",
  "admin_limitado",
  "mozo",
  "cocina",
  "caja",
  "soporte_mozzo",
];

const OPERATIONAL_ROLE_KEYS = new Set(["mozo", "cocina"]);

const formatDate = (value) => {
  if (!value) {
    return "Sin fecha";
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Sin fecha"
    : date.toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });
};

const resolveRoleKey = (person) =>
  resolveMozzoInternalUserRoleKey({
    isOwner: person?.roleKey === "owner",
    role: person?.role,
    workspaceRole: person?.workspace_role || person?.workspaceRole,
  });

const getPrimaryBranchId = (person) =>
  person?.primaryBranchId ||
  person?.branchAccesses?.find((access) => access?.active)?.branchId ||
  person?.branchAccesses?.[0]?.branchId ||
  "";

const getOperationalStaffState = (person, branchId, localStates = {}) => {
  const effectiveBranchId = branchId || getPrimaryBranchId(person);
  const localKey = `${effectiveBranchId}:${person?.user_id || ""}`;

  if (typeof localStates[localKey] === "boolean") {
    return localStates[localKey];
  }

  const branchAccess = person?.branchAccesses?.find(
    (access) => access?.branchId === effectiveBranchId
  );

  return person?.active !== false && (!branchAccess || branchAccess.active !== false);
};

const getPersonName = (person) =>
  person?.profileFullName ||
  person?.display_name ||
  person?.email ||
  "Personal sin nombre";

const createPersonnelForm = (branchId = "", roleKey = "mozo") => ({
  roleKey,
  email: "",
  fullName: "",
  branchId,
  ...buildMozzoRoleBranchPermissions(roleKey),
});

const createLocalTestForm = (branchId = "") => ({
  roleKey: "mozo",
  emailMode: "none",
  email: "",
  fullName: "",
  branchId,
  ...buildMozzoRoleBranchPermissions("mozo"),
});

const getRequestLabel = (request) => {
  switch (request?.requestType) {
    case STAFF_CHANGE_TYPES.DEACTIVATE:
      return "Baja de personal";
    case STAFF_CHANGE_TYPES.REACTIVATE:
      return "Reactivacion de personal";
    case STAFF_CHANGE_TYPES.UPDATE:
      return "Cambio de asignacion";
    default:
      return "Alta de personal";
  }
};

const getRequestStatusLabel = (status) => {
  switch (status) {
    case STAFF_CHANGE_STATUSES.APPROVED:
      return "Aprobada";
    case STAFF_CHANGE_STATUSES.REJECTED:
      return "Rechazada";
    default:
      return "Pendiente";
  }
};

const buildLocalPersonnelPreview = (branches = []) => {
  const firstBranch = branches[0] || {
    id: "creator-local-branch",
    name: "Sucursal local",
  };

  const buildPerson = (id, fullName, workspaceRole, active = true) => ({
    user_id: id,
    role: workspaceRole === "admin_general" ? "admin_general" : "admin_limitado",
    workspace_role: workspaceRole,
    active,
    profileFullName: fullName,
    primaryBranchId:
      workspaceRole === "admin_general" ? "" : firstBranch.id,
    primaryBranchName:
      workspaceRole === "admin_general" ? "Empresa completa" : firstBranch.name,
    branchAccesses:
      workspaceRole === "admin_general"
        ? []
        : [
            {
              branchId: firstBranch.id,
              branchName: firstBranch.name,
              active,
              permissions: {},
            },
          ],
  });

  return [
    buildPerson("local-general", "Admin general local", "admin_general"),
    buildPerson("local-manager", "Encargado de sucursal", "encargado_sucursal"),
    buildPerson("local-limited", "Admin limitado", "admin_limitado"),
    buildPerson("local-waiter", "Mozo de prueba", "mozo"),
    buildPerson("local-kitchen", "Cocina de prueba", "cocina"),
  ];
};

function BranchPermissionsEditor({
  permissions,
  onChange,
  canEditPermission = () => true,
  disabled = false,
  title = "Permisos por sucursal",
  description = "Los accesos marcados son el limite operativo de este integrante.",
}) {
  return (
    <div style={styles.permissionsPanel}>
      <div style={styles.permissionsHeader}>
        <div>
          <strong style={styles.permissionsTitle}>{title}</strong>
          <p style={styles.permissionsDescription}>{description}</p>
        </div>
      </div>

      <div style={styles.permissionsGrid}>
        {MOZZO_BRANCH_PERMISSION_GROUPS.map((group) => {
          const viewEnabled = group.viewKey
            ? permissions?.[group.viewKey] === true ||
              permissions?.[group.manageKey] === true
            : permissions?.[group.manageKey] === true;
          const manageEnabled = permissions?.[group.manageKey] === true;
          const canEditView = !group.viewKey || canEditPermission(group.viewKey);
          const canEditManage = canEditPermission(group.manageKey);
          const statusLabel = group.viewKey
            ? manageEnabled
              ? "Gestion total"
              : viewEnabled
                ? "Solo lectura"
                : "Sin acceso"
            : manageEnabled
              ? "Habilitado"
              : "Sin acceso";

          return (
            <article key={group.id} style={styles.permissionCard}>
              <div style={styles.permissionCardHeader}>
                <strong style={styles.permissionCardTitle}>{group.label}</strong>
                <span
                  style={{
                    ...styles.permissionStatus,
                    ...(manageEnabled
                      ? styles.permissionStatusManage
                      : viewEnabled
                        ? styles.permissionStatusView
                        : styles.permissionStatusOff),
                  }}
                >
                  {statusLabel}
                </span>
              </div>
              <p style={styles.permissionCardDescription}>{group.description}</p>
              <div style={styles.permissionActions}>
                {group.viewKey && (
                  <button
                    type="button"
                    style={{
                      ...styles.permissionButton,
                      ...(viewEnabled ? styles.permissionButtonActive : {}),
                    }}
                    disabled={disabled || !canEditView}
                    onClick={() => onChange(group, "viewKey", !viewEnabled)}
                  >
                    Ver
                  </button>
                )}
                <button
                  type="button"
                  style={{
                    ...styles.permissionButton,
                    ...(manageEnabled ? styles.permissionButtonManage : {}),
                  }}
                  disabled={disabled || !canEditManage}
                  onClick={() => onChange(group, "manageKey", !manageEnabled)}
                >
                  {group.viewKey ? "Gestionar" : "Habilitar"}
                </button>
              </div>
              {(!canEditView || !canEditManage) && (
                <small style={styles.permissionInheritedNote}>
                  Limitado por los permisos de quien solicita.
                </small>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}

function Personal() {
  const navigate = useNavigate();
  const {
    assignableInternalUserRoles,
    availableBranches,
    canAccessModule,
    canPerformAction,
    currentAccessRole,
    currentAccessRoleLabel,
    currentBranch,
    currentCompany,
    enterLocalPersonnelTestProfile,
    hasBranchPermission,
    isCreatorMode,
    isLocalPersonnelTestMode,
    isSupabaseConfigured,
    user,
  } = useAccessControl();
  const [selectedBranchId, setSelectedBranchId] = useState(currentBranch?.id || "");
  const [personnel, setPersonnel] = useState([]);
  const [requests, setRequests] = useState([]);
  const [requestStorage, setRequestStorage] = useState("local");
  const [localOperationalStates, setLocalOperationalStates] = useState({});
  const [formOpen, setFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [personnelForm, setPersonnelForm] = useState(
    createPersonnelForm(currentBranch?.id || "")
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [localTestProfiles, setLocalTestProfiles] = useState([]);
  const [localTestForm, setLocalTestForm] = useState(
    createLocalTestForm(currentBranch?.id || "")
  );
  const [localTestError, setLocalTestError] = useState("");
  const showLocalTestHarness = isLocalPersonnelTestHarnessEnabled();

  const canViewPersonnel =
    canAccessModule("personal") && canPerformAction("view_personnel");
  const canManagePersonnelDirectly = canPerformAction("manage_personnel_directly");
  const canRequestPersonnelChanges = canPerformAction("request_personnel_changes");
  const canReviewPersonnelRequests = canPerformAction("review_personnel_requests");
  const canToggleOperationalStaff = canPerformAction("toggle_operational_staff");
  // The creator workspace is only a browser preview and must never write to Supabase.
  const shouldUseLocalPreview = isCreatorMode || !isSupabaseConfigured;
  const effectiveSelectedBranchId =
    selectedBranchId || currentBranch?.id || availableBranches[0]?.id || "";
  const selectedBranch =
    availableBranches.find((branch) => branch.id === effectiveSelectedBranchId) || null;

  useEffect(() => {
    if (!selectedBranchId && currentBranch?.id) {
      setSelectedBranchId(currentBranch.id);
    }
  }, [currentBranch?.id, selectedBranchId]);

  useEffect(() => {
    if (!showLocalTestHarness || !currentCompany?.id) {
      return;
    }

    setLocalTestProfiles(listLocalPersonnelTestProfiles(currentCompany.id));
    setLocalTestForm((currentForm) => ({
      ...currentForm,
      branchId:
        currentForm.branchId || currentBranch?.id || availableBranches[0]?.id || "",
    }));
  }, [
    availableBranches,
    currentBranch?.id,
    currentCompany?.id,
    showLocalTestHarness,
  ]);

  const ensureAction = useCallback(
    (allowed, actionLabel) => {
      if (allowed) {
        return true;
      }

      setSuccessMessage("");
      setErrorMessage(
        `${currentAccessRoleLabel} no tiene permiso para ${actionLabel} dentro de Personal.`
      );
      return false;
    },
    [currentAccessRoleLabel]
  );

  const loadPersonnelData = useCallback(async () => {
    if (!currentCompany?.id || !canViewPersonnel) {
      setPersonnel([]);
      setRequests([]);
      return;
    }

    setLoading(true);

    try {
      const usersResult = shouldUseLocalPreview
        ? { users: buildLocalPersonnelPreview(availableBranches) }
        : canManagePersonnelDirectly
          ? { users: await listCompanyUsers(currentCompany.id, { includeInactive: true }) }
          : await listBranchPersonnel(effectiveSelectedBranchId);
      const requestsResult = await listPersonnelChangeRequests(currentCompany.id, {
        branchId: canManagePersonnelDirectly ? "" : effectiveSelectedBranchId,
        localOnly: shouldUseLocalPreview,
      });

      setPersonnel(usersResult.users || []);
      setRequests(requestsResult.requests || []);
      setRequestStorage(requestsResult.storage || "local");
      setLocalOperationalStates(getLocalOperationalStaffStates(currentCompany.id));
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.message || "No se pudo cargar el personal de la empresa.");
    } finally {
      setLoading(false);
    }
  }, [
    availableBranches,
    canManagePersonnelDirectly,
    canViewPersonnel,
    currentCompany?.id,
    effectiveSelectedBranchId,
    shouldUseLocalPreview,
  ]);

  useEffect(() => {
    void loadPersonnelData();
  }, [loadPersonnelData]);

  const roleOptions = useMemo(() => {
    const roleOptionsForCurrentAccess =
      listMozzoAssignableInternalUserRolesForAccessRole(currentAccessRole);

    if (roleOptionsForCurrentAccess.length > 0) {
      return roleOptionsForCurrentAccess;
    }

    return canManagePersonnelDirectly
      ? assignableInternalUserRoles.filter((role) => role.key !== "owner")
      : [];
  }, [assignableInternalUserRoles, canManagePersonnelDirectly, currentAccessRole]);

  const canDelegatePermission = useCallback(
    (permissionKey) =>
      canDelegateMozzoBranchPermission(permissionKey, {
        canGrantAll: canManagePersonnelDirectly,
        hasBranchPermission,
      }),
    [canManagePersonnelDirectly, hasBranchPermission]
  );

  const buildRoleForm = useCallback(
    (roleKey, branchId, identity = {}) => ({
      ...createPersonnelForm(branchId, roleKey),
      ...buildDelegatedMozzoRoleBranchPermissions(roleKey, identity, {
        canGrantAll: canManagePersonnelDirectly,
        hasBranchPermission,
      }),
      email: identity.email || "",
      fullName: identity.fullName || "",
      branchId,
      roleKey,
    }),
    [canManagePersonnelDirectly, hasBranchPermission]
  );

  const visiblePersonnel = useMemo(() => {
    const scopedPersonnel = canManagePersonnelDirectly
      ? personnel
      : personnel.filter(
          (person) =>
            getPrimaryBranchId(person) === effectiveSelectedBranchId ||
            person?.branchAccesses?.some(
              (access) => access?.branchId === effectiveSelectedBranchId
            )
        );

    const filteredPersonnel = canManagePersonnelDirectly && selectedBranchId
      ? scopedPersonnel.filter(
          (person) =>
            getPrimaryBranchId(person) === selectedBranchId ||
            resolveRoleKey(person) === "admin_general"
        )
      : scopedPersonnel;

    return filteredPersonnel
      .filter((person) =>
        canViewMozzoPersonnelRole(currentAccessRole, resolveRoleKey(person), {
          isSelf: person?.user_id === user?.id,
        })
      )
      .sort((left, right) => {
        const leftIndex = ROLE_ORDER.indexOf(resolveRoleKey(left));
        const rightIndex = ROLE_ORDER.indexOf(resolveRoleKey(right));
        return leftIndex - rightIndex || getPersonName(left).localeCompare(getPersonName(right));
      });
  }, [
    canManagePersonnelDirectly,
    currentAccessRole,
    effectiveSelectedBranchId,
    personnel,
    selectedBranchId,
    user?.id,
  ]);

  const visibleRequests = useMemo(() => {
    if (
      canReviewPersonnelRequests ||
      currentAccessRole === "encargado_sucursal"
    ) {
      return requests;
    }

    return requests.filter((request) => request.requestedBy === user?.id);
  }, [canReviewPersonnelRequests, currentAccessRole, requests, user?.id]);
  const pendingRequests = useMemo(
    () =>
      visibleRequests.filter(
        (request) => request.status === STAFF_CHANGE_STATUSES.PENDING
      ),
    [visibleRequests]
  );
  const activeCount = visiblePersonnel.filter((person) => {
    const branchId = getPrimaryBranchId(person) || effectiveSelectedBranchId;
    return getOperationalStaffState(person, branchId, localOperationalStates);
  }).length;
  const pausedCount = Math.max(0, visiblePersonnel.length - activeCount);

  const resetForm = useCallback(
    (branchId = effectiveSelectedBranchId) => {
      setPersonnelForm(createPersonnelForm(branchId));
      setEditingUser(null);
      setFormOpen(false);
    },
    [effectiveSelectedBranchId]
  );

  const openCreateForm = () => {
    const defaultRole = roleOptions.some((role) => role.key === "mozo")
      ? "mozo"
      : roleOptions[0]?.key || "mozo";
    setPersonnelForm(buildRoleForm(defaultRole, effectiveSelectedBranchId));
    setEditingUser(null);
    setFormOpen(true);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleLocalTestProfileSubmit = (event) => {
    event.preventDefault();
    setLocalTestError("");

    if (!localTestForm.fullName.trim()) {
      setLocalTestError("Carga un nombre visible para reconocer este perfil.");
      return;
    }

    if (!localTestForm.branchId) {
      setLocalTestError("Selecciona la sucursal donde trabajara este perfil.");
      return;
    }

    if (localTestForm.emailMode === "email" && !localTestForm.email.trim()) {
      setLocalTestError("Carga el correo o selecciona la opcion Ninguno.");
      return;
    }

    const branch = availableBranches.find(
      (candidate) => candidate.id === localTestForm.branchId
    );

    try {
      const savedProfile = saveLocalPersonnelTestProfile(currentCompany?.id, {
        fullName: localTestForm.fullName,
        email: localTestForm.emailMode === "email" ? localTestForm.email : "",
        roleKey: localTestForm.roleKey,
        branchId: localTestForm.branchId,
        branchName: branch?.name || "Sucursal local",
        permissions: buildMozzoRoleBranchPermissions(
          localTestForm.roleKey,
          localTestForm
        ),
      });
      setLocalTestProfiles((currentProfiles) => [savedProfile, ...currentProfiles]);
      setLocalTestForm(createLocalTestForm(localTestForm.branchId));
    } catch (error) {
      setLocalTestError(error.message || "No se pudo crear el perfil local.");
    }
  };

  const handleEnterLocalTestProfile = (profile) => {
    setLocalTestError("");
    const entered = enterLocalPersonnelTestProfile(profile);

    if (!entered) {
      setLocalTestError("No se pudo iniciar la prueba. Revisa el rol y la sucursal.");
      return;
    }

    if (profile.roleKey === "mozo") {
      navigate("/mozo");
      return;
    }

    if (profile.roleKey === "cocina") {
      navigate("/cocina");
      return;
    }

    navigate("/app");
  };

  const handleRemoveLocalTestProfile = (profile) => {
    const confirmed = window.confirm(
      `Eliminar a ${profile.fullName} del banco local de pruebas?`
    );

    if (!confirmed) {
      return;
    }

    setLocalTestProfiles(
      removeLocalPersonnelTestProfile(currentCompany?.id, profile.id)
    );
  };

  const openEditForm = (person) => {
    const roleKey = resolveRoleKey(person);
    const canRequestPermissionUpdate =
      currentAccessRole === "encargado_sucursal" && roleKey === "admin_limitado";

    if (
      !ensureAction(
        canManagePersonnelDirectly || canRequestPermissionUpdate,
        canRequestPermissionUpdate
          ? "solicitar cambios de permisos"
          : "editar personal"
      )
    ) {
      return;
    }

    const branchId = getPrimaryBranchId(person) || effectiveSelectedBranchId;
    const branchAccess = person?.branchAccesses?.find(
      (access) => access?.branchId === branchId
    );
    setPersonnelForm(
      buildRoleForm(roleKey, branchId, {
      email: person?.email || "",
      fullName: person?.profileFullName || "",
        ...(branchAccess?.permissions || {}),
      })
    );
    setEditingUser(person);
    setFormOpen(true);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const updateRole = (roleKey) => {
    const nextRole = getMozzoInternalUserRole(roleKey);
    setPersonnelForm((previousForm) =>
      buildRoleForm(
        roleKey,
        nextRole?.scope === "branch" ? previousForm.branchId : "",
        {
          email: previousForm.email,
          fullName: previousForm.fullName,
        }
      )
    );
  };

  const handlePersonnelSubmit = async (event) => {
    event.preventDefault();
    const isEdit = Boolean(editingUser?.user_id);
    const isRequestedPermissionEdit =
      isEdit &&
      !canManagePersonnelDirectly &&
      currentAccessRole === "encargado_sucursal" &&
      resolveRoleKey(editingUser) === "admin_limitado";
    const allowed =
      canManagePersonnelDirectly ||
      (canRequestPersonnelChanges && (!isEdit || isRequestedPermissionEdit));

    if (!ensureAction(allowed, isEdit ? "editar personal" : "registrar una alta")) {
      return;
    }

    if (
      !isEdit &&
      !canAssignMozzoInternalUserRole(currentAccessRole, personnelForm.roleKey)
    ) {
      setErrorMessage(
        `${currentAccessRoleLabel} no puede asignar el rol ${getMozzoInternalUserRoleLabel(
          personnelForm.roleKey
        )}.`
      );
      return;
    }

    const roleDefinition = getMozzoInternalUserRole(personnelForm.roleKey);
    const branchId =
      roleDefinition?.scope === "branch"
        ? canRequestPersonnelChanges && !canManagePersonnelDirectly
          ? effectiveSelectedBranchId
          : personnelForm.branchId
        : "";

    if (!isEdit && !personnelForm.email.trim()) {
      setErrorMessage("Necesitas cargar el correo del integrante.");
      return;
    }

    if (roleDefinition?.scope === "branch" && !branchId) {
      setErrorMessage("Necesitas asignar una sucursal al integrante.");
      return;
    }

    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const delegatedPermissions = buildDelegatedMozzoRoleBranchPermissions(
        personnelForm.roleKey,
        personnelForm,
        {
          canGrantAll: canManagePersonnelDirectly,
          hasBranchPermission,
        }
      );
      const payload = {
        ...personnelForm,
        ...delegatedPermissions,
        branchId,
        fullName: personnelForm.fullName.trim(),
        email: personnelForm.email.trim().toLowerCase(),
      };

      if (canManagePersonnelDirectly) {
        if (shouldUseLocalPreview) {
          const previewUser = {
            user_id: editingUser?.user_id || `local-${Date.now()}`,
            role: payload.roleKey === "admin_general" ? "admin_general" : "admin_limitado",
            workspace_role: payload.roleKey,
            active: true,
            profileFullName: payload.fullName || payload.email,
            email: payload.email,
            primaryBranchId: branchId,
            primaryBranchName:
              availableBranches.find((branch) => branch.id === branchId)?.name ||
              "Empresa completa",
            branchAccesses: branchId
              ? [
                  {
                    branchId,
                    branchName:
                      availableBranches.find((branch) => branch.id === branchId)?.name ||
                      "Sucursal",
                    active: true,
                    permissions: buildMozzoRoleBranchPermissions(payload.roleKey, payload),
                  },
                ]
              : [],
          };
          setPersonnel((previousUsers) =>
            editingUser
              ? previousUsers.map((person) =>
                  person.user_id === editingUser.user_id ? previewUser : person
                )
              : [previewUser, ...previousUsers]
          );
          setSuccessMessage(
            isEdit
              ? "Cambio aplicado en la vista local de Personal."
              : "Alta simulada en localhost. No se envio una invitacion real."
          );
        } else if (isEdit) {
          await updateCompanyUserAccess(currentCompany.id, editingUser.user_id, payload);
          setSuccessMessage("Personal actualizado correctamente.");
          await loadPersonnelData();
        } else {
          const invitation = await createInternalUserInvitation(currentCompany.id, user?.id, payload);
          setSuccessMessage(
            invitation.invitation_email_sent === false
              ? invitation.invitation_email_error || "La invitacion fue creada, pero no se envio el correo."
              : "Invitacion creada y enviada al integrante."
          );
          await loadPersonnelData();
        }
      } else {
        const requestResult = await createPersonnelChangeRequest({
          companyId: currentCompany.id,
          branchId,
          requestedBy: user?.id,
          requestedByName: user?.email || currentAccessRoleLabel,
          requestType: isRequestedPermissionEdit
            ? STAFF_CHANGE_TYPES.UPDATE
            : STAFF_CHANGE_TYPES.CREATE,
          targetUserId: isRequestedPermissionEdit ? editingUser.user_id : "",
          payload,
          localOnly: shouldUseLocalPreview,
        });
        setRequests((previousRequests) => [requestResult.request, ...previousRequests]);
        setRequestStorage(requestResult.storage);
        setSuccessMessage(
          requestResult.storage === "remote"
            ? isRequestedPermissionEdit
              ? "Solicitud de permisos enviada al admin general para aprobacion."
              : "Solicitud enviada al admin general para aprobacion."
            : isRequestedPermissionEdit
              ? "Solicitud de permisos guardada en localhost para validar la aprobacion."
              : "Solicitud guardada en localhost para validar el flujo de aprobacion."
        );
      }

      resetForm(branchId);
    } catch (error) {
      setErrorMessage(error.message || "No se pudo guardar el cambio de personal.");
    } finally {
      setSaving(false);
    }
  };

  const handleRequestDeactivation = async (person) => {
    if (!ensureAction(canRequestPersonnelChanges, "solicitar una baja")) {
      return;
    }

    const targetRoleKey = resolveRoleKey(person);
    if (!canAssignMozzoInternalUserRole(currentAccessRole, targetRoleKey)) {
      setErrorMessage(
        `${currentAccessRoleLabel} no puede solicitar la baja de ${getMozzoInternalUserRoleLabel(
          targetRoleKey
        )}.`
      );
      return;
    }

    const confirmed = window.confirm(
      `Se enviara una solicitud para dar de baja a ${getPersonName(person)}. El admin general debe aprobarla.`
    );

    if (!confirmed) {
      return;
    }

    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const requestResult = await createPersonnelChangeRequest({
        companyId: currentCompany.id,
        branchId: effectiveSelectedBranchId,
        requestedBy: user?.id,
        requestedByName: user?.email || currentAccessRoleLabel,
        requestType: STAFF_CHANGE_TYPES.DEACTIVATE,
        targetUserId: person.user_id,
        payload: {
          fullName: getPersonName(person),
          roleKey: targetRoleKey,
          branchId: effectiveSelectedBranchId,
        },
        localOnly: shouldUseLocalPreview,
      });
      setRequests((previousRequests) => [requestResult.request, ...previousRequests]);
      setRequestStorage(requestResult.storage);
      setSuccessMessage(
        requestResult.storage === "remote"
          ? "Solicitud de baja enviada para aprobacion."
          : "Solicitud de baja guardada en localhost para validar el flujo."
      );
    } catch (error) {
      setErrorMessage(error.message || "No se pudo solicitar la baja del integrante.");
    } finally {
      setSaving(false);
    }
  };

  const handleDirectDeactivation = async (person) => {
    if (!ensureAction(canManagePersonnelDirectly, "desactivar personal")) {
      return;
    }

    const confirmed = window.confirm(
      `Vas a desactivar el acceso de ${getPersonName(person)}. Podras reactivarlo luego.`
    );

    if (!confirmed) {
      return;
    }

    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      if (shouldUseLocalPreview) {
        setPersonnel((previousUsers) =>
          previousUsers.map((currentPerson) =>
            currentPerson.user_id === person.user_id
              ? { ...currentPerson, active: false }
              : currentPerson
          )
        );
        setSuccessMessage("Personal pausado en la vista local.");
      } else {
        await deactivateCompanyUser(currentCompany.id, person.user_id);
        await loadPersonnelData();
        setSuccessMessage("Personal desactivado correctamente.");
      }
    } catch (error) {
      setErrorMessage(error.message || "No se pudo desactivar el integrante.");
    } finally {
      setSaving(false);
    }
  };

  const handleReactivate = async (person) => {
    if (!ensureAction(canManagePersonnelDirectly, "reactivar personal")) {
      return;
    }

    const roleKey = resolveRoleKey(person);
    const branchId = getPrimaryBranchId(person);
    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      if (shouldUseLocalPreview) {
        setPersonnel((previousUsers) =>
          previousUsers.map((currentPerson) =>
            currentPerson.user_id === person.user_id
              ? { ...currentPerson, active: true }
              : currentPerson
          )
        );
        setSuccessMessage("Personal reactivado en la vista local.");
      } else {
        await updateCompanyUserAccess(currentCompany.id, person.user_id, {
          roleKey,
          branchId,
          active: true,
          ...buildMozzoRoleBranchPermissions(roleKey),
        });
        await loadPersonnelData();
        setSuccessMessage("Personal reactivado correctamente.");
      }
    } catch (error) {
      setErrorMessage(error.message || "No se pudo reactivar el integrante.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleOperationalStaff = async (person) => {
    if (!ensureAction(canToggleOperationalStaff, "activar o pausar personal operativo")) {
      return;
    }

    const roleKey = resolveRoleKey(person);
    const branchId = getPrimaryBranchId(person) || effectiveSelectedBranchId;
    const stateKey = `${branchId}:${person.user_id}`;

    if (!OPERATIONAL_ROLE_KEYS.has(roleKey) || !branchId) {
      setErrorMessage("Solo se pueden pausar o reactivar mozos y cocina de la sucursal.");
      return;
    }

    const currentlyActive = getOperationalStaffState(
      person,
      branchId,
      localOperationalStates
    );
    const nextActive = !currentlyActive;

    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const result = await setOperationalStaffActive({
        companyId: currentCompany.id,
        branchId,
        targetUserId: person.user_id,
        active: nextActive,
        localOnly: shouldUseLocalPreview,
      });
      setLocalOperationalStates((previousStates) => ({
        ...previousStates,
        [stateKey]: nextActive,
      }));
      setPersonnel((previousUsers) =>
        previousUsers.map((currentPerson) =>
          currentPerson.user_id === person.user_id
            ? { ...currentPerson, active: nextActive }
            : currentPerson
        )
      );
      setSuccessMessage(
        result.storage === "remote"
          ? `${getPersonName(person)} quedo ${nextActive ? "activo" : "pausado"}.`
          : `${getPersonName(person)} quedo ${nextActive ? "activo" : "pausado"} en localhost.`
      );
    } catch (error) {
      setErrorMessage(error.message || "No se pudo actualizar el estado del integrante.");
    } finally {
      setSaving(false);
    }
  };

  const handleReviewRequest = async (request, decision) => {
    if (!ensureAction(canReviewPersonnelRequests, "revisar solicitudes de personal")) {
      return;
    }

    if (decision === STAFF_CHANGE_STATUSES.REJECTED) {
      const confirmed = window.confirm("Vas a rechazar esta solicitud de personal.");
      if (!confirmed) {
        return;
      }
    }

    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const result = await reviewPersonnelChangeRequest({
        companyId: currentCompany.id,
        requestId: request.id,
        decision,
        reviewedBy: user?.id,
        localOnly: shouldUseLocalPreview,
      });
      setRequests((previousRequests) =>
        previousRequests.map((currentRequest) =>
          currentRequest.id === request.id ? result.request : currentRequest
        )
      );
      setRequestStorage(result.storage);
      const delivery =
        result.applied && result.invitation
          ? await deliverApprovedPersonnelInvitation(result.invitation)
          : null;
      setSuccessMessage(
        result.applied
          ? delivery?.message ||
            `Solicitud ${decision === STAFF_CHANGE_STATUSES.APPROVED ? "aprobada" : "rechazada"} y aplicada.`
          : `Solicitud ${decision === STAFF_CHANGE_STATUSES.APPROVED ? "aprobada" : "rechazada"} en localhost. El patch de Supabase aplicara el cambio real.`
      );
    } catch (error) {
      setErrorMessage(error.message || "No se pudo revisar la solicitud.");
    } finally {
      setSaving(false);
    }
  };

  if (!canViewPersonnel) {
    return (
      <section style={styles.restrictedCard}>
        <h1 style={styles.title}>Personal restringido</h1>
        <p style={styles.description}>
          Esta cuenta no tiene acceso al modulo de Personal de la sucursal activa.
        </p>
      </section>
    );
  }

  const isManagerFlow = canRequestPersonnelChanges && !canManagePersonnelDirectly;
  const isLimitedFlow = currentAccessRole === "admin_limitado";

  return (
    <section style={styles.page}>
      <header style={styles.hero}>
        <div>
          <p style={styles.eyebrow}>Equipo y jerarquia</p>
          <h1 style={styles.title}>Personal</h1>
          <p style={styles.description}>
            {canManagePersonnelDirectly
              ? "Administra la dotacion completa de la empresa, sus sucursales y las aprobaciones pendientes."
              : currentAccessRole === "encargado_sucursal"
                ? "Coordina la sucursal y propone admins limitados, mozos o cocina. Las altas, bajas y cesiones de permisos requieren aprobacion del admin general."
                : isManagerFlow
                  ? "Opera con los permisos aprobados y puede solicitar mozos o cocina. El admin general conserva la aprobacion final."
                : "Activa o pausa mozos y cocina de tu sucursal sin cambiar roles, permisos ni datos sensibles."}
          </p>
        </div>
        {(canManagePersonnelDirectly || canRequestPersonnelChanges) &&
          roleOptions.length > 0 && (
          <button type="button" style={styles.primaryButton} onClick={openCreateForm}>
            {canManagePersonnelDirectly ? "+ Agregar personal" : "+ Solicitar alta"}
          </button>
        )}
      </header>

      {requestStorage === "local" && (
        <div style={styles.localNotice}>
          Modo localhost: las solicitudes y pausas de Personal se guardan en este navegador
          hasta aplicar el patch de Supabase. Sirve para validar todo el flujo sin tocar beta.
        </div>
      )}

      {errorMessage && <div style={styles.errorNotice}>{errorMessage}</div>}
      {successMessage && <div style={styles.successNotice}>{successMessage}</div>}

      {showLocalTestHarness && !isLocalPersonnelTestMode && (
        <section style={styles.testHarnessCard}>
          <div style={styles.testHarnessHeader}>
            <div>
              <div style={styles.testHarnessBadge}>Solo localhost</div>
              <h2 style={styles.testHarnessTitle}>Banco de pruebas de personal</h2>
              <p style={styles.testHarnessDescription}>
                Crea identidades ficticias y entra con sus permisos reales. No se envian
                correos, no se crean usuarios y estos perfiles nunca aparecen en beta ni
                produccion.
              </p>
            </div>
            <span style={styles.testHarnessCount}>
              {localTestProfiles.length} perfiles
            </span>
          </div>

          <form onSubmit={handleLocalTestProfileSubmit} style={styles.testHarnessForm}>
            <label style={styles.field}>
              <span>Rol operativo</span>
              <select
                value={localTestForm.roleKey}
                onChange={(event) =>
                  setLocalTestForm((currentForm) => ({
                    ...currentForm,
                    roleKey: event.target.value,
                    ...buildMozzoRoleBranchPermissions(event.target.value),
                  }))
                }
                style={styles.input}
              >
                {assignableInternalUserRoles.map((role) => (
                  <option key={role.key} value={role.key}>
                    {role.label}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.field}>
              <span>Correo</span>
              <select
                value={localTestForm.emailMode}
                onChange={(event) =>
                  setLocalTestForm((currentForm) => ({
                    ...currentForm,
                    emailMode: event.target.value,
                    email: event.target.value === "none" ? "" : currentForm.email,
                  }))
                }
                style={styles.input}
              >
                <option value="none">Ninguno - prueba sin correo</option>
                <option value="email">Cargar un correo</option>
              </select>
            </label>

            {localTestForm.emailMode === "email" && (
              <label style={styles.field}>
                <span>Direccion de correo</span>
                <input
                  type="email"
                  value={localTestForm.email}
                  onChange={(event) =>
                    setLocalTestForm((currentForm) => ({
                      ...currentForm,
                      email: event.target.value,
                    }))
                  }
                  placeholder="persona@prueba.local"
                  style={styles.input}
                />
              </label>
            )}

            <label style={styles.field}>
              <span>Nombre visible</span>
              <input
                type="text"
                value={localTestForm.fullName}
                onChange={(event) =>
                  setLocalTestForm((currentForm) => ({
                    ...currentForm,
                    fullName: event.target.value,
                  }))
                }
                placeholder="Ejemplo: Fulano"
                style={styles.input}
              />
            </label>

            <label style={styles.field}>
              <span>Sucursal</span>
              <select
                value={localTestForm.branchId}
                onChange={(event) =>
                  setLocalTestForm((currentForm) => ({
                    ...currentForm,
                    branchId: event.target.value,
                  }))
                }
                style={styles.input}
              >
                <option value="">Selecciona sucursal</option>
                {availableBranches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </label>

            {getMozzoInternalUserRole(localTestForm.roleKey)
              ?.allowsCustomBranchPermissions && (
              <div style={styles.testPermissionsWrap}>
                <BranchPermissionsEditor
                  permissions={localTestForm}
                  onChange={(group, field, enabled) =>
                    setLocalTestForm((currentForm) =>
                      updateMozzoBranchPermissionGroup(
                        currentForm,
                        group,
                        field,
                        enabled
                      )
                    )
                  }
                  title={`Permisos de ${getMozzoInternalUserRoleLabel(
                    localTestForm.roleKey
                  )}`}
                  description="Solo afecta este perfil ficticio y permite comprobar la navegacion real en localhost."
                />
              </div>
            )}

            <button type="submit" style={styles.testHarnessCreateButton}>
              + Cargar personal de prueba
            </button>
          </form>

          {localTestError && <div style={styles.testHarnessError}>{localTestError}</div>}

          <div style={styles.testProfileList}>
            {localTestProfiles.length === 0 ? (
              <div style={styles.testHarnessEmpty}>
                Todavia no cargaste perfiles. Crea a Fulano como mozo, encargado o admin
                limitado para comenzar a comparar sus accesos.
              </div>
            ) : (
              localTestProfiles.map((profile) => (
                <article key={profile.id} style={styles.testProfileCard}>
                  <div style={styles.testProfileAvatar}>
                    {profile.fullName.slice(0, 1).toUpperCase()}
                  </div>
                  <div style={styles.testProfileIdentity}>
                    <small style={styles.testProfilePrefix}>Ingresar con</small>
                    <strong style={styles.testProfileName}>{profile.fullName}</strong>
                    <span style={styles.testProfileMeta}>
                      {getMozzoInternalUserRoleLabel(profile.roleKey)} · {profile.branchName}
                    </span>
                    <span style={styles.testProfileEmail}>
                      {profile.email || "Sin correo"}
                    </span>
                  </div>
                  <div style={styles.testProfileActions}>
                    <button
                      type="button"
                      style={styles.testProfileEnterButton}
                      onClick={() => handleEnterLocalTestProfile(profile)}
                    >
                      Ingresar
                    </button>
                    <button
                      type="button"
                      style={styles.testProfileDeleteButton}
                      onClick={() => handleRemoveLocalTestProfile(profile)}
                      aria-label={`Eliminar perfil de ${profile.fullName}`}
                    >
                      Eliminar
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>
        </section>
      )}

      <div style={styles.toolbar}>
        <div style={styles.branchFilterWrap}>
          <label htmlFor="personnel-branch" style={styles.filterLabel}>
            {canManagePersonnelDirectly ? "Vista de sucursal" : "Sucursal asignada"}
          </label>
          <select
            id="personnel-branch"
            value={canManagePersonnelDirectly ? selectedBranchId : effectiveSelectedBranchId}
            onChange={(event) => setSelectedBranchId(event.target.value)}
            disabled={!canManagePersonnelDirectly || saving}
            style={styles.select}
          >
            {canManagePersonnelDirectly && <option value="">Toda la empresa</option>}
            {availableBranches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
        </div>
        <button type="button" style={styles.refreshButton} onClick={() => void loadPersonnelData()}>
          Actualizar
        </button>
      </div>

      <div style={styles.summaryGrid}>
        <article style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Personal visible</span>
          <strong style={styles.summaryValue}>{visiblePersonnel.length}</strong>
          <span style={styles.summaryDetail}>
            {selectedBranch?.name || "Empresa completa"}
          </span>
        </article>
        <article style={styles.summaryCard}>
          <span style={styles.summaryLabel}>En servicio</span>
          <strong style={{ ...styles.summaryValue, color: "#15803d" }}>{activeCount}</strong>
          <span style={styles.summaryDetail}>Accesos activos</span>
        </article>
        <article style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Pausados</span>
          <strong style={{ ...styles.summaryValue, color: "#b45309" }}>{pausedCount}</strong>
          <span style={styles.summaryDetail}>Sin operar hoy</span>
        </article>
        <article style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Solicitudes</span>
          <strong style={{ ...styles.summaryValue, color: "#7c3aed" }}>{pendingRequests.length}</strong>
          <span style={styles.summaryDetail}>Aprobacion pendiente</span>
        </article>
      </div>

      {formOpen && (
        <form onSubmit={handlePersonnelSubmit} style={styles.formCard}>
          <div style={styles.formHeader}>
            <div>
              <p style={styles.eyebrow}>{editingUser ? "Edicion directa" : isManagerFlow ? "Solicitud de alta" : "Alta directa"}</p>
              <h2 style={styles.sectionTitle}>
                {editingUser ? "Editar integrante" : "Datos del integrante"}
              </h2>
            </div>
            <button type="button" style={styles.textButton} onClick={() => resetForm()}>
              Cerrar
            </button>
          </div>

          <div style={styles.formGrid}>
            <label style={styles.field}>
              <span>Rol operativo</span>
              <select
                value={personnelForm.roleKey}
                onChange={(event) => updateRole(event.target.value)}
                style={styles.input}
                disabled={saving || Boolean(editingUser)}
              >
                {roleOptions.map((role) => (
                  <option key={role.key} value={role.key}>
                    {role.label}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.field}>
              <span>Correo</span>
              <input
                type="email"
                value={personnelForm.email}
                onChange={(event) =>
                  setPersonnelForm((currentForm) => ({
                    ...currentForm,
                    email: event.target.value,
                  }))
                }
                placeholder="persona@correo.com"
                style={styles.input}
                disabled={saving || Boolean(editingUser)}
              />
            </label>

            <label style={styles.field}>
              <span>Nombre visible</span>
              <input
                type="text"
                value={personnelForm.fullName}
                onChange={(event) =>
                  setPersonnelForm((currentForm) => ({
                    ...currentForm,
                    fullName: event.target.value,
                  }))
                }
                placeholder="Nombre y apellido"
                style={styles.input}
                disabled={saving}
              />
            </label>

            {getMozzoInternalUserRole(personnelForm.roleKey)?.scope === "branch" ? (
              <label style={styles.field}>
                <span>Sucursal</span>
                <select
                  value={
                    isManagerFlow ? effectiveSelectedBranchId : personnelForm.branchId
                  }
                  onChange={(event) =>
                    setPersonnelForm((currentForm) => ({
                      ...currentForm,
                      branchId: event.target.value,
                    }))
                  }
                  style={styles.input}
                  disabled={saving || isManagerFlow}
                >
                  <option value="">Selecciona sucursal</option>
                  {availableBranches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <div style={styles.companyRoleInfo}>
                Este rol trabaja sobre toda la empresa, no sobre una sola sucursal.
              </div>
            )}
          </div>

          <div style={styles.roleInfo}>
            {getMozzoInternalUserRole(personnelForm.roleKey)?.description}
          </div>

          {getMozzoInternalUserRole(personnelForm.roleKey)
            ?.allowsCustomBranchPermissions && (
            <BranchPermissionsEditor
              permissions={personnelForm}
              onChange={(group, field, enabled) =>
                setPersonnelForm((currentForm) =>
                  updateMozzoBranchPermissionGroup(
                    currentForm,
                    group,
                    field,
                    enabled
                  )
                )
              }
              canEditPermission={canDelegatePermission}
              disabled={saving}
              title={
                personnelForm.roleKey === "encargado_sucursal"
                  ? "Permisos del Encargado"
                  : "Permisos del Admin limitado"
              }
              description={
                canManagePersonnelDirectly
                  ? "El admin general define el alcance exacto dentro de la sucursal."
                  : "Puedes proponer solamente permisos que tu propia cuenta ya tiene. El admin general debe aprobarlos."
              }
            />
          )}

          <div style={styles.formActions}>
            <button type="button" style={styles.secondaryButton} onClick={() => resetForm()} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" style={styles.primaryButton} disabled={saving}>
              {saving
                ? "Guardando..."
                : editingUser
                  ? "Guardar cambios"
                  : isManagerFlow
                    ? "Enviar para aprobacion"
                    : "Crear invitacion"}
            </button>
          </div>
        </form>
      )}

      <section style={styles.sectionCard}>
        <div style={styles.sectionHeader}>
          <div>
            <p style={styles.eyebrow}>Dotacion activa</p>
            <h2 style={styles.sectionTitle}>Personal por sucursal</h2>
          </div>
          {loading && <span style={styles.loadingLabel}>Actualizando...</span>}
        </div>

        {visiblePersonnel.length === 0 ? (
          <div style={styles.emptyState}>
            Todavia no hay personal visible para esta sucursal. Cuando se aplique el patch,
            encargados y admins limitados veran automaticamente la dotacion asignada.
          </div>
        ) : (
          <div style={styles.personnelGrid}>
            {visiblePersonnel.map((person) => {
              const roleKey = resolveRoleKey(person);
              const branchId = getPrimaryBranchId(person) || effectiveSelectedBranchId;
              const active = getOperationalStaffState(
                person,
                branchId,
                localOperationalStates
              );
              const canToggleThisUser =
                canToggleOperationalStaff &&
                OPERATIONAL_ROLE_KEYS.has(roleKey) &&
                Boolean(branchId) &&
                (!isLimitedFlow || branchId === effectiveSelectedBranchId);

              return (
                <article key={person.user_id} style={styles.personCard}>
                  <div style={styles.personTopLine}>
                    <div style={styles.personIdentity}>
                      <div
                        style={{
                          ...styles.avatar,
                          backgroundColor: active ? "#dcfce7" : "#fef3c7",
                          color: active ? "#166534" : "#92400e",
                        }}
                      >
                        {getPersonName(person).slice(0, 1).toUpperCase()}
                      </div>
                      <div>
                        <h3 style={styles.personName}>{getPersonName(person)}</h3>
                        <p style={styles.personMeta}>
                          {getMozzoInternalUserRoleLabel(roleKey)}
                        </p>
                      </div>
                    </div>
                    <span
                      style={{
                        ...styles.statusPill,
                        ...(active ? styles.statusActive : styles.statusPaused),
                      }}
                    >
                      {active ? "Activo" : "Pausado"}
                    </span>
                  </div>

                  <div style={styles.personDetails}>
                    <span>{person.primaryBranchName || "Empresa completa"}</span>
                    {person.profilePhone && <span>{person.profilePhone}</span>}
                    {person.email && <span>{person.email}</span>}
                  </div>

                  <div style={styles.cardActions}>
                    {canManagePersonnelDirectly && roleKey !== "owner" && (
                      <button
                        type="button"
                        style={styles.secondaryButton}
                        onClick={() => openEditForm(person)}
                        disabled={saving}
                      >
                        Editar
                      </button>
                    )}
                    {!canManagePersonnelDirectly &&
                      currentAccessRole === "encargado_sucursal" &&
                      roleKey === "admin_limitado" && (
                        <button
                          type="button"
                          style={styles.secondaryButton}
                          onClick={() => openEditForm(person)}
                          disabled={saving}
                        >
                          Solicitar permisos
                        </button>
                      )}
                    {canToggleThisUser && (
                      <button
                        type="button"
                        style={active ? styles.pauseButton : styles.resumeButton}
                        onClick={() => void handleToggleOperationalStaff(person)}
                        disabled={saving}
                      >
                        {active ? "Pausar" : "Activar"}
                      </button>
                    )}
                    {canManagePersonnelDirectly && roleKey !== "owner" && !active && (
                      <button
                        type="button"
                        style={styles.resumeButton}
                        onClick={() => void handleReactivate(person)}
                        disabled={saving}
                      >
                        Reactivar acceso
                      </button>
                    )}
                    {canManagePersonnelDirectly && roleKey !== "owner" && active && (
                      <button
                        type="button"
                        style={styles.dangerButton}
                        onClick={() => void handleDirectDeactivation(person)}
                        disabled={saving}
                      >
                        Desactivar
                      </button>
                    )}
                    {isManagerFlow &&
                      canAssignMozzoInternalUserRole(currentAccessRole, roleKey) && (
                      <button
                        type="button"
                        style={styles.dangerButton}
                        onClick={() => void handleRequestDeactivation(person)}
                        disabled={saving}
                      >
                        Solicitar baja
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {(canReviewPersonnelRequests || canRequestPersonnelChanges) && (
        <section style={styles.sectionCard}>
          <div style={styles.sectionHeader}>
            <div>
              <p style={styles.eyebrow}>Circuito de autorizacion</p>
              <h2 style={styles.sectionTitle}>Solicitudes de personal</h2>
            </div>
            <span style={styles.pendingBadge}>{pendingRequests.length} pendientes</span>
          </div>

          {visibleRequests.length === 0 ? (
            <div style={styles.emptyState}>No hay solicitudes de personal registradas.</div>
          ) : (
            <div style={styles.requestList}>
              {visibleRequests.map((request) => {
                const requestRoleKey = request.payload?.roleKey || "admin_limitado";
                const requestBranch =
                  availableBranches.find((branch) => branch.id === request.branchId)?.name ||
                  "Sucursal";

                return (
                  <article key={request.id} style={styles.requestCard}>
                    <div style={styles.requestTopLine}>
                      <div>
                        <strong>{getRequestLabel(request)}</strong>
                        <p style={styles.requestMeta}>
                          {request.payload?.fullName || "Personal"} - {getMozzoInternalUserRoleLabel(requestRoleKey)} - {requestBranch}
                        </p>
                      </div>
                      <span
                        style={{
                          ...styles.statusPill,
                          ...(request.status === STAFF_CHANGE_STATUSES.APPROVED
                            ? styles.statusActive
                            : request.status === STAFF_CHANGE_STATUSES.REJECTED
                              ? styles.statusRejected
                              : styles.statusPending),
                        }}
                      >
                        {getRequestStatusLabel(request.status)}
                      </span>
                    </div>
                    <p style={styles.requestMeta}>
                      Solicitada por {request.requestedByName || "Encargado"} el {formatDate(request.createdAt)}
                    </p>
                    {request.reason && <p style={styles.requestReason}>{request.reason}</p>}
                    {request.reviewNote && <p style={styles.requestReason}>Revision: {request.reviewNote}</p>}
                    {canReviewPersonnelRequests && request.status === STAFF_CHANGE_STATUSES.PENDING && (
                      <div style={styles.cardActions}>
                        <button
                          type="button"
                          style={styles.resumeButton}
                          onClick={() =>
                            void handleReviewRequest(request, STAFF_CHANGE_STATUSES.APPROVED)
                          }
                          disabled={saving}
                        >
                          Aprobar
                        </button>
                        <button
                          type="button"
                          style={styles.dangerButton}
                          onClick={() =>
                            void handleReviewRequest(request, STAFF_CHANGE_STATUSES.REJECTED)
                          }
                          disabled={saving}
                        >
                          Rechazar
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>
      )}
    </section>
  );
}

const styles = {
  page: {
    width: "min(1220px, 100%)",
    margin: "0 auto",
    padding: "34px clamp(18px, 3vw, 42px) 64px",
    boxSizing: "border-box",
    color: "#172033",
  },
  hero: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: "20px",
    padding: "28px",
    borderRadius: "24px",
    background:
      "linear-gradient(135deg, color-mix(in srgb, var(--brand-primary, #c2410c) 13%, #fff) 0%, #fff7ed 46%, color-mix(in srgb, var(--brand-secondary, #f97316) 16%, #fff) 100%)",
    border: "1px solid color-mix(in srgb, var(--brand-primary, #c2410c) 20%, #fed7aa)",
  },
  eyebrow: {
    margin: 0,
    color: "var(--brand-primary, #c2410c)",
    textTransform: "uppercase",
    letterSpacing: "0.09em",
    fontWeight: 800,
    fontSize: "11px",
  },
  title: { margin: "7px 0 10px", fontSize: "clamp(30px, 4vw, 44px)", lineHeight: 1.03 },
  description: { margin: 0, maxWidth: "720px", color: "#526079", lineHeight: 1.55 },
  primaryButton: {
    border: "none",
    borderRadius: "12px",
    padding: "12px 18px",
    background: "var(--brand-primary, #c2410c)",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  secondaryButton: {
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    padding: "9px 12px",
    background: "#fff",
    color: "#334155",
    fontWeight: 700,
    cursor: "pointer",
  },
  refreshButton: {
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    padding: "10px 14px",
    background: "#fff",
    color: "#334155",
    fontWeight: 700,
    cursor: "pointer",
  },
  textButton: {
    border: "none",
    background: "transparent",
    color: "#475569",
    fontWeight: 700,
    cursor: "pointer",
  },
  pauseButton: {
    border: "1px solid #f59e0b",
    borderRadius: "10px",
    padding: "9px 12px",
    background: "#fffbeb",
    color: "#92400e",
    fontWeight: 800,
    cursor: "pointer",
  },
  resumeButton: {
    border: "1px solid #86efac",
    borderRadius: "10px",
    padding: "9px 12px",
    background: "#f0fdf4",
    color: "#166534",
    fontWeight: 800,
    cursor: "pointer",
  },
  dangerButton: {
    border: "1px solid #fecaca",
    borderRadius: "10px",
    padding: "9px 12px",
    background: "#fff1f2",
    color: "#be123c",
    fontWeight: 800,
    cursor: "pointer",
  },
  localNotice: {
    marginTop: "18px",
    padding: "13px 16px",
    borderRadius: "14px",
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    lineHeight: 1.45,
    fontWeight: 600,
  },
  errorNotice: {
    marginTop: "18px",
    padding: "13px 16px",
    borderRadius: "14px",
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    color: "#be123c",
    lineHeight: 1.45,
  },
  successNotice: {
    marginTop: "18px",
    padding: "13px 16px",
    borderRadius: "14px",
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    color: "#166534",
    lineHeight: 1.45,
  },
  testHarnessCard: {
    marginTop: "22px",
    padding: "24px",
    borderRadius: "22px",
    border: "1px solid #fdba74",
    background:
      "radial-gradient(circle at top right, rgba(251, 146, 60, 0.18), transparent 34%), linear-gradient(145deg, #fffaf5 0%, #ffffff 62%)",
    boxShadow: "0 18px 38px rgba(124, 45, 18, 0.08)",
  },
  testHarnessHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "16px",
  },
  testHarnessBadge: {
    display: "inline-flex",
    padding: "5px 9px",
    borderRadius: "999px",
    background: "#ffedd5",
    color: "#c2410c",
    fontSize: "10px",
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  testHarnessTitle: {
    margin: "10px 0 6px",
    color: "#431407",
    fontSize: "24px",
  },
  testHarnessDescription: {
    maxWidth: "760px",
    margin: 0,
    color: "#7c2d12",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  testHarnessCount: {
    padding: "8px 11px",
    borderRadius: "12px",
    background: "#fff",
    border: "1px solid #fed7aa",
    color: "#9a3412",
    fontSize: "12px",
    fontWeight: 850,
  },
  testHarnessForm: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    alignItems: "end",
    gap: "12px",
    marginTop: "20px",
    padding: "17px",
    borderRadius: "16px",
    background: "rgba(255, 255, 255, 0.88)",
    border: "1px solid #ffedd5",
  },
  testHarnessCreateButton: {
    minHeight: "42px",
    border: "none",
    borderRadius: "11px",
    padding: "10px 14px",
    background: "#ea580c",
    color: "#fff",
    fontWeight: 850,
    cursor: "pointer",
  },
  testHarnessError: {
    marginTop: "12px",
    padding: "11px 13px",
    borderRadius: "11px",
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    color: "#be123c",
    fontSize: "13px",
  },
  testProfileList: {
    display: "grid",
    gap: "10px",
    marginTop: "16px",
  },
  testHarnessEmpty: {
    padding: "22px",
    borderRadius: "14px",
    border: "1px dashed #fdba74",
    color: "#9a3412",
    background: "rgba(255, 247, 237, 0.68)",
    fontSize: "13px",
    lineHeight: 1.5,
    textAlign: "center",
  },
  testProfileCard: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    padding: "13px",
    borderRadius: "14px",
    border: "1px solid #fed7aa",
    background: "#fff",
  },
  testProfileAvatar: {
    width: "42px",
    height: "42px",
    flex: "0 0 42px",
    display: "grid",
    placeItems: "center",
    borderRadius: "13px",
    background: "#ffedd5",
    color: "#c2410c",
    fontWeight: 950,
  },
  testProfileIdentity: {
    minWidth: "170px",
    flex: "1 1 260px",
    display: "grid",
    gap: "2px",
  },
  testProfilePrefix: {
    color: "#c2410c",
    fontSize: "9px",
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  testProfileName: { color: "#431407", fontSize: "15px" },
  testProfileMeta: { color: "#7c2d12", fontSize: "12px", fontWeight: 700 },
  testProfileEmail: { color: "#a8a29e", fontSize: "11px" },
  testProfileActions: { display: "flex", flexWrap: "wrap", gap: "7px" },
  testProfileEnterButton: {
    border: "none",
    borderRadius: "10px",
    padding: "9px 14px",
    background: "#431407",
    color: "#fff",
    fontWeight: 850,
    cursor: "pointer",
  },
  testProfileDeleteButton: {
    border: "1px solid #fecaca",
    borderRadius: "10px",
    padding: "9px 11px",
    background: "#fff",
    color: "#be123c",
    fontWeight: 750,
    cursor: "pointer",
  },
  toolbar: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: "14px",
    marginTop: "22px",
  },
  branchFilterWrap: { display: "grid", gap: "7px", minWidth: "min(100%, 330px)" },
  filterLabel: { color: "#64748b", fontSize: "12px", fontWeight: 800 },
  select: {
    minWidth: "220px",
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    padding: "11px 12px",
    background: "#fff",
    color: "#172033",
    fontWeight: 650,
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: "14px",
    marginTop: "20px",
  },
  summaryCard: {
    display: "grid",
    gap: "5px",
    padding: "18px",
    borderRadius: "18px",
    background: "#fff",
    border: "1px solid #e2e8f0",
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.045)",
  },
  summaryLabel: { color: "#64748b", fontSize: "12px", fontWeight: 800 },
  summaryValue: { color: "#172033", fontSize: "30px", lineHeight: 1 },
  summaryDetail: { color: "#94a3b8", fontSize: "12px" },
  formCard: {
    marginTop: "22px",
    padding: "24px",
    borderRadius: "20px",
    background: "#fff",
    border: "1px solid #dbeafe",
    boxShadow: "0 16px 34px rgba(15, 23, 42, 0.055)",
  },
  formHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "18px",
  },
  sectionTitle: { margin: "5px 0 0", color: "#172033", fontSize: "22px" },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "14px",
  },
  field: { display: "grid", gap: "7px", color: "#475569", fontSize: "13px", fontWeight: 750 },
  input: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    padding: "11px 12px",
    background: "#fff",
    color: "#172033",
    font: "inherit",
  },
  companyRoleInfo: {
    display: "flex",
    alignItems: "center",
    padding: "12px",
    borderRadius: "10px",
    background: "#f8fafc",
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.4,
  },
  roleInfo: {
    marginTop: "14px",
    padding: "12px 14px",
    borderRadius: "12px",
    background: "#f8fafc",
    color: "#475569",
    lineHeight: 1.45,
    fontSize: "13px",
  },
  testPermissionsWrap: {
    gridColumn: "1 / -1",
  },
  permissionsPanel: {
    marginTop: "16px",
    padding: "18px",
    borderRadius: "16px",
    border: "1px solid #fed7aa",
    background: "linear-gradient(145deg, #fffaf5 0%, #ffffff 70%)",
  },
  permissionsHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "14px",
  },
  permissionsTitle: {
    color: "#431407",
    fontSize: "15px",
  },
  permissionsDescription: {
    margin: "5px 0 0",
    color: "#7c2d12",
    fontSize: "12px",
    lineHeight: 1.45,
  },
  permissionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: "10px",
  },
  permissionCard: {
    display: "grid",
    alignContent: "start",
    gap: "9px",
    minWidth: 0,
    padding: "13px",
    borderRadius: "13px",
    border: "1px solid #e2e8f0",
    background: "rgba(255, 255, 255, 0.94)",
  },
  permissionCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  permissionCardTitle: {
    color: "#172033",
    fontSize: "13px",
  },
  permissionCardDescription: {
    minHeight: "34px",
    margin: 0,
    color: "#64748b",
    fontSize: "11px",
    lineHeight: 1.45,
  },
  permissionStatus: {
    flex: "0 0 auto",
    padding: "4px 7px",
    borderRadius: "999px",
    fontSize: "9px",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  permissionStatusManage: { background: "#dcfce7", color: "#166534" },
  permissionStatusView: { background: "#dbeafe", color: "#1d4ed8" },
  permissionStatusOff: { background: "#f1f5f9", color: "#64748b" },
  permissionActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: "7px",
  },
  permissionButton: {
    border: "1px solid #cbd5e1",
    borderRadius: "9px",
    padding: "7px 10px",
    background: "#fff",
    color: "#475569",
    fontSize: "11px",
    fontWeight: 800,
    cursor: "pointer",
  },
  permissionButtonActive: {
    borderColor: "#93c5fd",
    background: "#eff6ff",
    color: "#1d4ed8",
  },
  permissionButtonManage: {
    borderColor: "#86efac",
    background: "#f0fdf4",
    color: "#166534",
  },
  permissionInheritedNote: {
    color: "#b45309",
    fontSize: "10px",
    lineHeight: 1.35,
  },
  formActions: { display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" },
  sectionCard: {
    marginTop: "22px",
    padding: "24px",
    borderRadius: "20px",
    background: "#fff",
    border: "1px solid #e2e8f0",
    boxShadow: "0 16px 34px rgba(15, 23, 42, 0.045)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "18px",
  },
  loadingLabel: { color: "#64748b", fontSize: "13px", fontWeight: 700 },
  pendingBadge: {
    padding: "7px 10px",
    borderRadius: "999px",
    background: "#f5f3ff",
    color: "#6d28d9",
    fontSize: "12px",
    fontWeight: 800,
  },
  emptyState: {
    padding: "28px",
    borderRadius: "14px",
    background: "#f8fafc",
    color: "#64748b",
    lineHeight: 1.5,
    textAlign: "center",
  },
  personnelGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(270px, 1fr))",
    gap: "14px",
  },
  personCard: {
    display: "grid",
    gap: "14px",
    padding: "18px",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    background: "linear-gradient(145deg, #fff 0%, #f8fafc 100%)",
  },
  personTopLine: { display: "flex", justifyContent: "space-between", gap: "12px" },
  personIdentity: { display: "flex", gap: "11px", minWidth: 0 },
  avatar: {
    width: "38px",
    height: "38px",
    borderRadius: "13px",
    display: "grid",
    placeItems: "center",
    fontWeight: 900,
    flexShrink: 0,
  },
  personName: { margin: 0, color: "#172033", fontSize: "16px", lineHeight: 1.2 },
  personMeta: { margin: "4px 0 0", color: "#64748b", fontSize: "12px", lineHeight: 1.35 },
  statusPill: {
    alignSelf: "flex-start",
    padding: "5px 8px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  statusActive: { background: "#dcfce7", color: "#166534" },
  statusPaused: { background: "#fef3c7", color: "#92400e" },
  statusPending: { background: "#fef3c7", color: "#92400e" },
  statusRejected: { background: "#ffe4e6", color: "#be123c" },
  personDetails: { display: "grid", gap: "4px", color: "#64748b", fontSize: "12px" },
  cardActions: { display: "flex", flexWrap: "wrap", gap: "8px" },
  requestList: { display: "grid", gap: "12px" },
  requestCard: {
    padding: "16px",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    background: "#fcfdff",
  },
  requestTopLine: { display: "flex", justifyContent: "space-between", gap: "14px" },
  requestMeta: { margin: "6px 0 0", color: "#64748b", fontSize: "13px", lineHeight: 1.45 },
  requestReason: { margin: "8px 0 0", color: "#475569", fontSize: "13px", lineHeight: 1.45 },
  restrictedCard: {
    maxWidth: "760px",
    margin: "44px auto",
    padding: "28px",
    borderRadius: "20px",
    background: "#fff",
    border: "1px solid #e2e8f0",
  },
};

export default Personal;
